<script setup lang="ts">
import SidebarResizeHandle from "./ui/SidebarResizeHandle.vue";
import { useSidebarWidthsStore } from "../stores/useSidebarWidthsStore";
const sidebarWidths = useSidebarWidthsStore();
import { computed, onMounted, watch } from "vue";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
} from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { useTea } from "../lib/tea";
import { useRoute } from "vue-router";
import ProjectSettingsDialog, {
  type ProjectSettingsSectionId,
} from "./ProjectSettingsDialog.vue";
import type { ProjectSettingsSavePayload } from "./ProjectSettingsDialog.update";
import SceneMoveCollisionDialog from "./SceneMoveCollisionDialog.vue";
import ThemeToggle from "./ThemeToggle.vue";
import { storeToRefs } from "pinia";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import {
  useOutlineActionsStore,
  type OutlineHeaderNewItem,
} from "../stores/useOutlineActionsStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useProjectSettingsStore } from "../stores/useProjectSettingsStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import { projectQuery } from "../lib/projectRouter";
import {
  useEditorNavStore,
  type NavSection,
} from "../stores/useEditorNavStore";
import { useShareModeStore } from "../stores/useShareModeStore";
import type { EditorActiveTab } from "../lib/tauri/core";
import type { Component } from "vue";
import type { RouteLocationRaw } from "vue-router";
import {
  initialOutlineChromeModel,
  openSettingsSection,
  updateOutlineChromeFn,
  type OutlineChromeCmd,
} from "./EditorOutlinePane.update";
import {
  IconGlobe,
  IconNotes,
  IconSearch,
  IconTimeline,
  IconTrash,
  IconWriting,
} from "./ribbonIcons";
import OutlineTree from "./outline/OutlineTree.vue";
import OutlineNotes from "./outline/OutlineNotes.vue";
import OutlineDatabase from "./outline/OutlineDatabase.vue";
import OutlineTrash from "./outline/OutlineTrash.vue";
import StoryboardPanel from "./outline/StoryboardPanel.vue";

/** Outline body by section. Driven here (not via named router-view) so changing
 * chapter/scene within Writing never remounts OutlineTree and loses scroll. */
const OUTLINE_BY_SECTION: Record<NavSection, Component> = {
  manuscript: OutlineTree,
  storyboard: StoryboardPanel,
  notes: OutlineNotes,
  database: OutlineDatabase,
  trash: OutlineTrash,
};

const OUTLINE_TABS: {
  id: EditorActiveTab;
  section: NavSection;
  label: string;
  icon: typeof IconWriting;
}[] = [
  {
    id: "manuscript",
    section: "manuscript",
    label: "Writing",
    icon: IconWriting,
  },
  {
    id: "storyboard",
    section: "storyboard",
    label: "Timeline",
    icon: IconTimeline,
  },
  { id: "notes", section: "notes", label: "Scratchpad", icon: IconNotes },
  {
    id: "database",
    section: "database",
    label: "Worldbuilding",
    icon: IconGlobe,
  },
  { id: "trash", section: "trash", label: "Trash", icon: IconTrash },
];

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const projectSettings = useProjectSettingsStore();
const outlineActions = useOutlineActionsStore();
const route = useRoute();
const nav = useEditorNavStore();
const share = useShareModeStore();
const { shareMode } = storeToRefs(share);

const {
  projectName,
  folderName,
  rootPath,
  mobileShell,
  outlineCollapsed,
  projectConfig,
  activeNote,
  activeDatabase,
  modLabel,
} = storeToRefs(session);
const config = computed(() => fromMaybe(projectConfig.value)!);

const entities = useOutlineEntitiesStore();
workspace.registerOnUnplacedChanged(() => {
  void entities.refreshUnplaced();
});

watch(activeNote, () => void entities.refreshNotes());
watch(activeDatabase, () => void entities.refreshDatabase());

const activeSection = computed(() => {
  const meta = route.meta.section;
  return typeof meta === "string" ? meta : null;
});

const outlinePane = computed((): Component => {
  const section = activeSection.value;
  if (section !== null && section in OUTLINE_BY_SECTION) {
    return OUTLINE_BY_SECTION[section as NavSection];
  }
  return OutlineTree;
});

const outlinePaneKey = computed(() => activeSection.value ?? "manuscript");

function openProjectSearch(): void {
  matchMaybe(outlineActions.cancelCreating, {
    nothing: () => {},
    just: (cancel) => {
      cancel();
    },
  });
  session.projectSearchOpen = true;
  if (session.mobileShell) session.outlineOpen = false;
}

function tabShortcutLabel(index: number): string {
  return `${modLabel.value}${index + 1}`;
}

function tabTitle(tab: (typeof OUTLINE_TABS)[number], index: number): string {
  return `${tab.label} (${tabShortcutLabel(index)})`;
}

const searchTitle = computed(() => `Search (${modLabel.value}⇧P)`);

function tabTo(tab: (typeof OUTLINE_TABS)[number]): RouteLocationRaw {
  const path = nav.routeForSection(tab.section);
  const query = projectQuery();
  return Object.keys(query).length > 0 ? { path, query } : path;
}

function runOutlineChromeCmd(cmd: OutlineChromeCmd): void {
  switch (cmd.kind) {
    case "cancelCreating":
      matchMaybe(outlineActions.cancelCreating, {
        nothing: () => {},
        just: (cancel) => {
          cancel();
        },
      });
      return;
    default:
      assertNever(cmd as never);
  }
}

const { model: chromeModel, dispatch: dispatchChrome } = useTea(
  initialOutlineChromeModel,
  updateOutlineChromeFn,
  { runCmd: just(runOutlineChromeCmd) },
);

const settingsOpen = computed(() => chromeModel.value.settingsOpen);
const settingsSection = computed(() => chromeModel.value.settingsSection);
const newMenuOpen = computed(() => chromeModel.value.newMenuOpen);

function openSettings(section?: ProjectSettingsSectionId): void {
  void dispatchChrome(
    openSettingsSection(
      typeof section === "undefined" ? nothing : just(section),
    ),
  );
}

function closeSettings(): void {
  void dispatchChrome({ type: "CloseSettings" });
}

// The manuscript outline can create more than one kind of thing, so its `+`
// opens a menu. Every other outline page still gets a plain button.
function onHeaderNewClick(): void {
  matchMaybe(outlineActions.headerNew, {
    nothing: () => {},
    just: (action) => {
      if (isNothing(action.items)) {
        action.run();
        return;
      }
      void dispatchChrome({ type: "ToggleNewMenu" });
    },
  });
}

function runNewMenuItem(item: OutlineHeaderNewItem): void {
  if (item.disabled === true) return;
  void dispatchChrome({ type: "CloseNewMenu" });
  item.run();
}

const headerNewAction = computed(() => outlineActions.headerNew);

// Switching outline pages swaps the action out from under an open menu.
watch(
  () => outlineActions.headerNew,
  () => {
    void dispatchChrome({ type: "CloseNewMenu" });
  },
);

onMounted(() => {
  projectSettings.registerOpenSettings(openSettings);
});

function onSaveSettings(payload: ProjectSettingsSavePayload): void {
  void projectSettings.updateSettings(payload);
}
</script>

<template>
  <aside
    class="zw-outline"
    :style="{ '--sidebar-width': `${sidebarWidths.widths.left}rem` }"
    :class="{
      'zw-outline-collapsed': outlineCollapsed && !mobileShell,
      'zw-outline--overlay': mobileShell,
    }"
    :aria-label="
      outlineCollapsed && !mobileShell
        ? 'Project outline (collapsed)'
        : 'Project outline'
    "
  >
    <button
      v-if="!mobileShell"
      type="button"
      class="zw-outline-expand"
      title="Show outline"
      aria-label="Show outline"
      :tabindex="outlineCollapsed ? 0 : -1"
      :aria-hidden="!outlineCollapsed"
      @click="outlineCollapsed = false"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        class="size-4"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M4 6h16M4 12h10M4 18h14"
        />
      </svg>
    </button>

    <div
      class="zw-outline-panel"
      :inert="outlineCollapsed && !mobileShell"
      :aria-hidden="outlineCollapsed && !mobileShell"
    >
      <header class="zw-outline-header project-header">
        <h2 :title="projectName">{{ projectName }}</h2>
        <div class="project-header-actions">
          <div
            v-if="isJust(headerNewAction) && shareMode === false"
            class="header-new"
          >
            <button
              type="button"
              class="zw-outline-action"
              :title="headerNewAction.value.label"
              :aria-label="headerNewAction.value.label"
              :aria-haspopup="
                isJust(headerNewAction.value.items) ? 'menu' : undefined
              "
              :aria-expanded="
                isJust(headerNewAction.value.items) ? newMenuOpen : undefined
              "
              @click="onHeaderNewClick"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                class="size-3.5"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 5v14M5 12h14"
                />
              </svg>
            </button>
            <ul
              v-if="newMenuOpen && isJust(headerNewAction.value.items)"
              class="header-new-menu"
              role="menu"
            >
              <li
                v-for="item in headerNewAction.value.items.value"
                :key="item.label"
              >
                <button
                  type="button"
                  role="menuitem"
                  class="header-new-item"
                  :disabled="item.disabled"
                  @click="runNewMenuItem(item)"
                >
                  {{ item.label }}
                </button>
              </li>
            </ul>
          </div>
          <button
            v-if="!shareMode"
            type="button"
            class="zw-outline-action"
            title="Project settings"
            aria-label="Project settings"
            @click="openSettings()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              class="size-3.5"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066Z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
              />
            </svg>
          </button>
          <button
            v-if="!mobileShell"
            type="button"
            class="zw-outline-hide"
            title="Hide outline"
            aria-label="Hide outline"
            @click="outlineCollapsed = true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              class="size-3.5"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 6 9 12l6 6"
              />
            </svg>
          </button>
        </div>
      </header>

      <div class="zw-outline-body">
        <!-- Section-keyed, not route-param-keyed: manuscript scene/chapter
             changes must not remount OutlineTree (that resets scroll). -->
        <keep-alive>
          <component
            :is="outlinePane"
            :key="outlinePaneKey"
            @request-close-settings="closeSettings"
          />
        </keep-alive>
      </div>
    </div>

    <footer class="zw-outline-footer">
      <div v-show="!outlineCollapsed && !shareMode" class="zw-outline-tabs">
        <nav class="zw-outline-tab-nav" aria-label="Outline views">
          <RouterLink
            v-for="(tab, index) in OUTLINE_TABS"
            :key="tab.id"
            :to="tabTo(tab)"
            class="zw-outline-tab"
            :class="{
              'zw-outline-tab-active': activeSection === tab.section,
            }"
            :aria-current="activeSection === tab.section ? 'page' : undefined"
            :title="tabTitle(tab, index)"
            :aria-label="tabTitle(tab, index)"
          >
            <component :is="tab.icon" class="size-4" aria-hidden="true" />
          </RouterLink>
        </nav>
        <button
          type="button"
          class="zw-outline-tab"
          :title="searchTitle"
          :aria-label="searchTitle"
          @click="openProjectSearch"
        >
          <IconSearch class="size-4" aria-hidden="true" />
        </button>
      </div>
      <ThemeToggle />
    </footer>

    <SceneMoveCollisionDialog
      :open="isJust(workspace.sceneMoveCollision)"
      :attempted-name="
        matchMaybe(workspace.sceneMoveCollision, {
          just: (c) => c.attemptedName,
          nothing: () => '',
        })
      "
      :chapter-name="
        matchMaybe(workspace.sceneMoveCollision, {
          just: (c) => c.chapterName,
          nothing: () => '',
        })
      "
      :error="
        matchMaybe(workspace.sceneMoveCollision, {
          just: (c) => c.error,
          nothing: () => nothing,
        })
      "
      @close="workspace.onCancelSceneMoveCollision"
      @retry="workspace.onResolveSceneMoveCollision"
    />

    <ProjectSettingsDialog
      :open="settingsOpen"
      :config="config"
      :folder-name="folderName"
      :root-path="rootPath"
      :initial-section="settingsSection"
      @close="closeSettings"
      @save="onSaveSettings"
    />
    <SidebarResizeHandle v-if="!mobileShell && !outlineCollapsed" side="left" />
  </aside>
</template>

<style scoped>
.zw-outline {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: clamp(
    14.5rem,
    var(--sidebar-width, 14.5rem),
    min(29rem, calc(50vw - 12rem))
  );
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid var(--line);
  background: color-mix(in oklch, var(--bg) 92%, var(--bg-raised));
  font-family: var(--font-ui);
}

.zw-outline--overlay {
  --sidebar-width: 14.5rem !important;
  border-right: 0;
  background: var(--bg);
}

.zw-outline-collapsed {
  width: 2.5rem;
}

.zw-outline-expand,
.zw-outline-action,
.zw-outline-hide {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    color var(--duration-fast) var(--ease-out-quart),
    opacity var(--duration-fast) var(--ease-out-quart),
    background-color var(--duration-fast) var(--ease-out-quart);
}

.zw-outline-expand {
  position: absolute;
  top: var(--space-md);
  left: 50%;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
}

.zw-outline-collapsed .zw-outline-expand {
  opacity: 1;
  pointer-events: auto;
}

.zw-outline-action {
  width: 1.55rem;
  height: 1.55rem;
  color: color-mix(in oklch, var(--text-muted) 88%, transparent);
}

.zw-outline-hide {
  width: 1.6rem;
  height: 1.6rem;
  margin-left: 0.1rem;
  color: var(--text-muted);
}

.zw-outline-expand:hover,
.zw-outline-action:hover,
.zw-outline-hide:hover {
  color: var(--text-strong);
}

.zw-outline-action:hover {
  background: color-mix(in oklch, var(--color-zw-amber) 20%, transparent);
}

.zw-outline-hide:hover {
  background: var(--hover-fill);
}

.zw-outline-expand:focus-visible,
.zw-outline-action:focus-visible,
.zw-outline-hide:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.zw-outline-panel {
  display: flex;
  flex-direction: column;
  width: clamp(
    14.5rem,
    var(--sidebar-width, 14.5rem),
    min(29rem, calc(50vw - 12rem))
  );
  min-height: 0;
  flex: 1;
  flex-shrink: 0;
  opacity: 1;
  transition: opacity var(--duration-fast) var(--ease-out-quart);
}

.zw-outline-collapsed .zw-outline-panel {
  opacity: 0;
  pointer-events: none;
}

.zw-outline-body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.project-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-md) var(--space-sm);
}

.zw-outline-tabs {
  display: flex;
  align-items: center;
  gap: 0.05rem;
  min-width: 0;
  flex: 1;
}

.zw-outline-tab-nav {
  display: flex;
  align-items: center;
  gap: 0.05rem;
  flex-shrink: 0;
}

.zw-outline-tab {
  appearance: none;
  border: 0;
  background: transparent;
  width: 1.7rem;
  height: 1.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.25rem;
  flex-shrink: 0;
  text-decoration: none;
}

.zw-outline-tab:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.zw-outline-tab-active {
  color: var(--color-zw-amber);
  background: color-mix(in oklch, var(--color-zw-amber) 20%, transparent);
}

.zw-outline-tab-active:hover {
  color: var(--color-zw-amber);
  background: color-mix(in oklch, var(--color-zw-amber) 26%, transparent);
}

.project-header h2 {
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.project-header-actions {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  flex-shrink: 0;
}

.header-new {
  position: relative;
  display: inline-flex;
}

.header-new-menu {
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  z-index: 20;
  min-width: 9rem;
  margin: 0;
  padding: 0.2rem;
  list-style: none;
  background: var(--bg-raised);
  border: 1px solid var(--border);
  border-radius: 0.35rem;
}

.header-new-item {
  appearance: none;
  border: 0;
  background: transparent;
  display: block;
  width: 100%;
  padding: 0.3rem 0.5rem;
  text-align: left;
  font: inherit;
  font-size: 0.88rem;
  color: var(--text);
  border-radius: 0.25rem;
  cursor: pointer;
}

.header-new-item:hover:not(:disabled) {
  background: var(--hover-fill);
  color: var(--text-strong);
}

.header-new-item:disabled {
  color: var(--text-muted);
  opacity: 0.6;
  cursor: default;
}

.zw-outline-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  flex-shrink: 0;
  width: 100%;
  z-index: 1;
  padding: 0.45rem 0.55rem 0.55rem;
  border-top: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
}

.zw-outline-collapsed .zw-outline-footer {
  justify-content: center;
  padding: 0.45rem 0 0.55rem;
  border-top-color: transparent;
}
</style>
